import React, { useEffect, useState } from 'react';
import {
  fetchBoardYears,
  fetchPastPresidents,
  upsertBoardYear,
  deleteBoardYear,
  addPastPresident,
  deletePastPresident
} from '../../services/api';

// Simple inline editable list for phase 1; can be enhanced with modals later.
const BoardManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [boardYears, setBoardYears] = useState([]);
  const [pastPresidents, setPastPresidents] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState('years');

  // New Year form state
  const emptyYear = { period: '', title: '', boardOfDirectors: [], boardOfTrustees: [], executiveCommittee: [], administration: [], sponsoringInstitution: '' };
  const [editingYear, setEditingYear] = useState(null); // store period string if editing
  const [yearForm, setYearForm] = useState(emptyYear);
  const [savingYear, setSavingYear] = useState(false);
  const [showYearModal, setShowYearModal] = useState(false);

  // Past President form state
  const [ppYears, setPpYears] = useState('');
  const [ppName, setPpName] = useState('');
  const [ppNote, setPpNote] = useState('');
  const [ppSaving, setPpSaving] = useState(false);
  const [ppUpdatingId, setPpUpdatingId] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [years, presidents] = await Promise.all([
        fetchBoardYears(),
        fetchPastPresidents()
      ]);
      setBoardYears(years);
      setPastPresidents(presidents);
    } catch (e) {
      setError(e.message || 'Failed to load board data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const startCreateYear = () => {
    setEditingYear(null);
    setYearForm(emptyYear);
    setShowYearModal(true);
  };

  const startCreateYearFromLastYear = () => {
    setEditingYear(null);
    if (boardYears.length > 0) {
      // Clone from the most recent year (first in sorted list)
      const lastYear = boardYears[0];
      setYearForm({
        ...emptyYear,
        period: '', // User must fill in new period
        title: '',  // User must fill in new title
        boardOfDirectors: JSON.parse(JSON.stringify(lastYear.boardOfDirectors || [])),
        boardOfTrustees: JSON.parse(JSON.stringify(lastYear.boardOfTrustees || [])),
        executiveCommittee: JSON.parse(JSON.stringify(lastYear.executiveCommittee || [])),
        administration: JSON.parse(JSON.stringify(lastYear.administration || [])),
        sponsoringInstitution: lastYear.sponsoringInstitution || ''
      });
    } else {
      setYearForm(emptyYear);
    }
    setShowYearModal(true);
  };

  const startEditYear = (year) => {
    setEditingYear(year.period);
    setYearForm({ ...emptyYear, ...year });
    setShowYearModal(true);
  };

  const handleYearFieldChange = (e) => {
    const { name, value } = e.target;
    setYearForm(f => ({ ...f, [name]: value }));
  };

  const saveYear = async (e) => {
    e.preventDefault();
    if (!yearForm.period) return alert('Period is required');
    try {
      setSavingYear(true);
      // Sanitize payload: remove any member rows that don't have a name (required by schema)
      const sanitizeArray = (arr) => Array.isArray(arr)
        ? arr.filter(item => item && (item.name || '').toString().trim() !== '')
        : [];

      const payload = {
        ...yearForm,
        boardOfDirectors: sanitizeArray(yearForm.boardOfDirectors),
        boardOfTrustees: sanitizeArray(yearForm.boardOfTrustees),
        executiveCommittee: sanitizeArray(yearForm.executiveCommittee),
        administration: sanitizeArray(yearForm.administration),
      };

      await upsertBoardYear(payload);
      await loadData();
      setEditingYear(null);
      setYearForm(emptyYear);
      setShowYearModal(false);
    } catch (e) {
      console.error('Save year error:', e);
      console.error('Error response:', e.response?.data);
      console.error('Payload sent:', payload);
      const errorMsg = e.response?.data?.message || e.message || 'Failed to save year';
      alert(errorMsg);
    } finally { setSavingYear(false); }
  };

  // Helper functions to manage member arrays
  const updateMemberArray = (key, index, field, value) => {
    setYearForm(f => {
      const copy = { ...f };
      const arr = [...(copy[key] || [])];
      arr[index] = { ...arr[index], [field]: value };
      copy[key] = arr;
      return copy;
    });
  };

  const addMemberRow = (key, template) => {
    setYearForm(f => ({ ...f, [key]: [...(f[key] || []), template] }));
  };

  const removeMemberRow = (key, index) => {
    setYearForm(f => ({ ...f, [key]: f[key].filter((_, i) => i !== index) }));
  };

  const removeYear = async (period) => {
    if (!window.confirm(`Delete board year ${period}?`)) return;
    try {
      await deleteBoardYear(period);
      setBoardYears(prev => prev.filter(y => y.period !== period));
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Failed to delete');
    }
  };

  const addPastPresidentHandler = async (e) => {
    e.preventDefault();
    if (!ppYears || !ppName) return alert('Years and Name required');
    try {
      setPpSaving(true);
      await addPastPresident({ years: ppYears, name: ppName, note: ppNote });
      setPpYears(''); setPpName(''); setPpNote('');
      await loadData();
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Failed to add');
    } finally { setPpSaving(false); }
  };

  const deletePastPresidentHandler = async (id) => {
    if (!window.confirm('Delete this past president entry?')) return;
    try {
      await deletePastPresident(id);
      setPastPresidents(prev => prev.filter(p => p._id !== id));
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Failed to delete past president');
    }
  };

  const updatePastPresidentNote = async (entry, note) => {
    try {
      setPpUpdatingId(entry._id);
      await addPastPresident({ years: entry.years, name: entry.name, note });
      setPastPresidents(prev => prev.map(p => p._id === entry._id ? { ...p, note } : p));
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Failed to update note');
    } finally {
      setPpUpdatingId(null);
    }
  };

  if (loading) return <div>Loading board data...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div>
      <h4 className="mb-3">Board Management</h4>
      <ul className="nav nav-pills mb-3">
        <li className="nav-item">
          <button className={`nav-link ${activeSubTab==='years'? 'active':''}`} onClick={() => setActiveSubTab('years')}>Board Years</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeSubTab==='past'? 'active':''}`} onClick={() => setActiveSubTab('past')}>Past Presidents</button>
        </li>
      </ul>

      {activeSubTab === 'years' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Board Years</h5>
            <div className="btn-group">
              <button className="btn btn-sm btn-primary" onClick={startCreateYear}>
                <i className="bi bi-plus-circle me-1"></i>
                Create Blank
              </button>
              {boardYears.length > 0 && (
                <button className="btn btn-sm btn-outline-primary" onClick={startCreateYearFromLastYear} title="Clone board members from the most recent year">
                  <i className="bi bi-files me-1"></i>
                  Clone from Last Year
                </button>
              )}
            </div>
          </div>

          {/* Year List */}
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th style={{width:'130px'}}>Period</th>
                <th>Navigation Menu Text</th>
                <th>Directors</th>
                <th>Trustees</th>
                <th>Exec Committee</th>
                <th>Admin</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {boardYears.map(y => (
                <tr key={y._id}>
                  <td>{y.period}</td>
                  <td>{y.title || y.period}</td>
                  <td>{y.boardOfDirectors?.length || 0}</td>
                  <td>{y.boardOfTrustees?.length || 0}</td>
                  <td>{y.executiveCommittee?.length || 0}</td>
                  <td>{y.administration?.length || 0}</td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => startEditYear(y)}>Edit</button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => removeYear(y.period)}>Delete</button>
                  </td>
                </tr>
              ))}
              {boardYears.length === 0 && <tr><td colSpan={7} className="text-center text-muted">No board years yet</td></tr>}
            </tbody>
          </table>

          {showYearModal && (
            <div className="modal d-block" tabIndex="-1" role="dialog" style={{background:'rgba(0,0,0,0.5)'}}>
              <div className="modal-dialog modal-xl">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{editingYear ? `Edit Board Year ${editingYear}` : 'Create Board Year'}</h5>
                    <button type="button" className="btn-close" onClick={()=>{setShowYearModal(false); setEditingYear(null);}}></button>
                  </div>
                  <form onSubmit={saveYear}>
                    <div className="modal-body">
                      <div className="row g-3 mb-3">
                        <div className="col-md-3">
                          <label className="form-label">Period *</label>
                          <input className="form-control form-control-sm" name="period" value={yearForm.period} onChange={handleYearFieldChange} placeholder="2024-2025" disabled={!!editingYear} />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Navigation Menu Text</label>
                          <input className="form-control form-control-sm" name="title" value={yearForm.title} onChange={handleYearFieldChange} placeholder="Display title" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Sponsoring Institution</label>
                          <input className="form-control form-control-sm" name="sponsoringInstitution" value={yearForm.sponsoringInstitution||''} onChange={handleYearFieldChange} />
                        </div>
                      </div>

                      {/* Sections */}
                      <SectionEditor title="Board of Directors" arrayKey="boardOfDirectors" data={yearForm.boardOfDirectors} updateMemberArray={updateMemberArray} addMemberRow={addMemberRow} removeMemberRow={removeMemberRow} template={{ position:'', term:'', name:'', affiliation:'' }} columns={['position','term','name','affiliation']} />
                      <SectionEditor title="Board of Trustees" arrayKey="boardOfTrustees" data={yearForm.boardOfTrustees} updateMemberArray={updateMemberArray} addMemberRow={addMemberRow} removeMemberRow={removeMemberRow} template={{ name:'', affiliation:'' }} columns={['name','affiliation']} />
                      <SectionEditor title="Executive Committee" arrayKey="executiveCommittee" data={yearForm.executiveCommittee} updateMemberArray={updateMemberArray} addMemberRow={addMemberRow} removeMemberRow={removeMemberRow} template={{ position:'', term:'', name:'', affiliation:'' }} columns={['position','term','name','affiliation']} />
                      <SectionEditor title="Administration" arrayKey="administration" data={yearForm.administration} updateMemberArray={updateMemberArray} addMemberRow={addMemberRow} removeMemberRow={removeMemberRow} template={{ position:'', name:'', affiliation:'' }} columns={['position','name','affiliation']} />
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={()=>{setShowYearModal(false); setEditingYear(null);}}>Close</button>
                      <button type="submit" className="btn btn-success" disabled={savingYear}>{savingYear? 'Saving...':'Save'}</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'past' && (
        <div>
          <h5 className="mb-3">Past Presidents</h5>
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th style={{width:'140px'}}>Years</th>
                <th>Name</th>
                <th>Note</th>
                <th style={{width:'80px'}}></th>
              </tr>
            </thead>
            <tbody>
              {pastPresidents.map(p => (
                <tr key={p._id}>
                  <td>{p.years}</td>
                  <td>{p.name}</td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={p.note || ''}
                      onChange={e => updatePastPresidentNote(p, e.target.value)}
                      disabled={ppUpdatingId === p._id}
                    >
                      <option value="">None</option>
                      <option value="Deceased">Deceased</option>
                    </select>
                  </td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-danger" onClick={() => deletePastPresidentHandler(p._id)}>Del</button>
                  </td>
                </tr>
              ))}
              {pastPresidents.length === 0 && <tr><td colSpan={4} className="text-center text-muted">No entries</td></tr>}
            </tbody>
          </table>

          <div className="card mt-3">
            <div className="card-body">
              <h6 className="card-title mb-3">Add Past President</h6>
              <form onSubmit={addPastPresidentHandler} className="row g-2">
                <div className="col-md-2">
                  <input className="form-control form-control-sm" placeholder="1995 – 1996" value={ppYears} onChange={e=>setPpYears(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <input className="form-control form-control-sm" placeholder="Name" value={ppName} onChange={e=>setPpName(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <select className="form-select form-select-sm" value={ppNote} onChange={e=>setPpNote(e.target.value)}>
                    <option value="">Note (optional)</option>
                    <option value="Deceased">Deceased</option>
                  </select>
                </div>
                <div className="col-md-3 d-flex">
                  <button type="submit" className="btn btn-sm btn-primary me-2" disabled={ppSaving}>{ppSaving? 'Adding...':'Add'}</button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={()=>{setPpYears('');setPpName('');setPpNote('');}}>Clear</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardManagement;

// Reusable section editor for member arrays
const SectionEditor = ({ title, arrayKey, data = [], updateMemberArray, addMemberRow, removeMemberRow, template, columns }) => {
  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">{title}</h6>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={()=>addMemberRow(arrayKey, template)}>Add</button>
      </div>
      <div className="table-responsive" style={{maxHeight:'260px', overflowY:'auto'}}>
        <table className="table table-sm align-middle mb-0">
          <thead className="table-light">
            <tr>
              {columns.map(col => <th key={col} style={{whiteSpace:'nowrap'}}>{col.charAt(0).toUpperCase()+col.slice(1)}</th>)}
              <th style={{width:'40px'}}></th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                {columns.map(col => (
                  <td key={col}>
                    <input
                      className="form-control form-control-sm"
                      value={row[col] || ''}
                      onChange={e=>updateMemberArray(arrayKey, idx, col, e.target.value)}
                    />
                  </td>
                ))}
                <td className="text-end">
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={()=>removeMemberRow(arrayKey, idx)}>&times;</button>
                </td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr><td colSpan={columns.length+1} className="text-center text-muted">No entries</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
